// core, form, route
import {Component, ElementRef, HostListener, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

// image cropper
import {ImageCroppedEvent, LoadedImage} from 'ngx-image-cropper';
import {DomSanitizer} from '@angular/platform-browser';

// models
import {ImageModel} from '../shared/models/image.model';
import {Profile} from '../update-my-profile/models/profile.model';

// services
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {UtilsService} from '../utils/utils.service';
import {UpdateProfileService} from '../update-my-profile/services/update-my-profile.service';
import {PreviousPageService} from '../shared/services/previous-page.service';
import {ImageTypeValidation} from '../shared/services/image-type-validation';
import {ImageSizeValidation} from '../shared/services/image-size-validation';

// constants
import {AppConstants} from '../utils/constants';

// UI
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';

// components
import {ConfirmationModal} from '../shared/dialogs/confirmation-modal/confirmation-modal.component';
import {UnsavedChangesComponent} from '../shared/unsaved-changes-component';

declare var $: any;
declare var translateApp;

@Component({
    selector: 'update-my-profile',
    templateUrl: './update-my-profile.component.html',
    styleUrls: ['./update-my-profile.component.scss'],
    standalone: false
})

export class UpdateMyProfileComponent implements UnsavedChangesComponent {

  @ViewChild('fileInput') myFileInput: ElementRef;

  public confirmationModalRef: BsModalRef;
  public postalCodeRequired = false;

  private updateMyProfileForm: UntypedFormGroup;
  private loggedInUserProfile: Profile = new Profile();

  // used for custom styling of the select component
  private selectWithInitialValue = false;
  private languages = AppConstants.languageCodes;
  private listOfLanguages: Array<any> = new Array<any>();
  private listOfCountries: Array<any> = new Array<any>();
  private titles = AppConstants.titleCodes;
  private listOfTitles: Array<any> = new Array<any>();
  private listOfRoles: Array<any> = new Array<any>();

  private removeAvatar: boolean;
  private companyLogoWidth: number = 100;

  private readonly: boolean = true;

  private newsletterVerificationPending: boolean = false;

  // variables for image cropper
  private imageFromDB: any = '';
  imageChangedEvent: any = '';
  base64Image: string = '';

  private homePath = AppConstants.homePage;

  private loading = false;

  private isNotDeleteNavigation: boolean = true;

  private filesModified: boolean = false;

  constructor(private _utilsService: UtilsService,
              private _formBuilder: UntypedFormBuilder,
              private _translateService: TranslateService,
              private _updateProfileService: UpdateProfileService,
              private _router: Router,
              private _previousPageService: PreviousPageService,
              private _modalService: BsModalService,
              private _sanitizer: DomSanitizer) {
    this.updateMyProfileForm = this._formBuilder.group({
      customerNr: [
        {
          value: '',
          disabled: false
        }
      ],
      company: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.maxLength(50)
        ])
      ],
      title: [
        {
          value: '',
          disabled: false
        }
      ],
      firstName: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20)
        ])
      ],
      lastName: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(40)
        ])
      ],
      email: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.pattern(this._utilsService.getEmailRegexPattern()),
          Validators.maxLength(50)
        ])
      ],
      birthDate: [
        {
          value: null,
          disabled: false
        }
      ],
      phone: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.pattern(this._utilsService.getPhoneNumberRegex()),
          Validators.maxLength(20)
        ])
      ],
      language: [
        {
          value: '',
          disabled: false
        },
        Validators.required
      ],
      countryCode: [
        {
          value: '',
          disabled: false
        },
        Validators.required
      ],
      postalCode: [
        {
          value: '',
          disabled: false
        },
        Validators.compose([
          Validators.maxLength(10),
          Validators.minLength(2),
          Validators.pattern(this._utilsService.getPostalCodeRegex())
        ])
      ],
      avatar: [
        {
          value: '',
          disabled: false
        },
      ],
      companyLogo: [
        {
          value: '',
          disabled: false
        }
      ],
      contactPerson: [
        {
          value: '',
          disabled: false
        }
      ],
      roles: [
        {
          value: '',
          disabled: false
        }
      ],
      wantsNewsletter: [
        {
          value: false,
          disabled: false
        }
      ]
    }, {
      validator: Validators.compose([
        ImageTypeValidation.ValidateType('avatar'),
        ImageSizeValidation.ValidateSize('avatar')
      ])
    });

    _translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this._utilsService.initializeSelectOrMultiselect2(this.titles).subscribe((response) => {
        this.listOfTitles = response as any;
      });

      this._utilsService.initializeSelectOrMultiselect2(this.languages).subscribe((response) => {
        this.listOfLanguages = response as any;
      });

      this._updateProfileService.getCountries().subscribe((response) => {
        this._utilsService.initializeSelectOrMultiselect2(response).subscribe((res) => {
          this.listOfCountries = res as any;
        });
      });

      if (this.loggedInUserProfile.roles) {
        this._utilsService.initializeSelectOrMultiselect2(this.loggedInUserProfile.roles).subscribe((response) => {
          this.listOfRoles = response as any;
        });
      }
    });
  }

  ngOnInit() {
    this.titles = this.titles.filter((title) =>
      !AppConstants.disabledSalutationCodes.includes(title)
    );
    this.listOfTitles = this._utilsService.initializeListOfSalutations(this.titles);

    this._utilsService.initializeSelectOrMultiselect2(this.languages).subscribe((response) => {
      this.listOfLanguages = response as any;
    });

    this._updateProfileService.getCountries().subscribe((response) => {
      this._utilsService.initializeSelectOrMultiselect2(response).subscribe((res) => {
        this.listOfCountries = res as any;
      });
    });


    if (this.updateMyProfileForm.controls.language) {
      this.selectWithInitialValue = true;
    }

    if (this.updateMyProfileForm.controls.countryCode) {
      this.selectWithInitialValue = true;
    }

    this._updateProfileService.fetchProfile().subscribe(res => {
      this.loggedInUserProfile = res;
      this.readonly = true == res.ldapUser;
      // this.newsletterVerificationPending = this.loggedInUserProfile.receiveNewsletter
      //   && this.loggedInUserProfile.agreedToReceiveNewsletter == null;

      this._updateProfileService.getProfilePicture().subscribe((avatarData: ImageModel) => {
        this.loggedInUserProfile.avatar = avatarData.content;
        this.imageFromDB = avatarData.content;
        this.loggedInUserProfile.avatarContentType = avatarData.contentType;
        this.loggedInUserProfile.avatarName = avatarData.name;
        this.loggedInUserProfile.avatarSizeInBytes = avatarData.sizeInBytes;
      });


      this._updateProfileService.getCustomerLogo().subscribe((companyLogo: ImageModel) => {
        this.loggedInUserProfile.companyLogo = companyLogo.content;
      });

      this._utilsService.initializeSelectOrMultiselect2(this.loggedInUserProfile.roles).subscribe((response) => {
        this.listOfRoles = response as any;
      });

      this._initFormWithExistingUserData();
      if (this.readonly) {
        this._disableForbiddenLdapUserFields();
      }
      if (this.loggedInUserProfile.customerNumber != null) {
        this._initAndDisableCompanyFields();
      }

    });


  }

  public getUpdateMyProfileForm() {
    return this.updateMyProfileForm;
  }

  public getLoggedInUserProfile() {
    return this.loggedInUserProfile;
  }

  public getSelectWithInitialValue() {
    return this.selectWithInitialValue;
  }

  public getListOfLanguages() {
    return this.listOfLanguages;
  }

  public getListOfCountries() {
    return this.listOfCountries;
  }

  public getListOfTitles() {
    return this.listOfTitles;
  }

  public getListOfRoles() {
    return this.listOfRoles;
  }

  public getCompanyLogoWidth() {
    return this.companyLogoWidth;
  }

  public getReadonly() {
    return this.readonly;
  }

  public getNewsletterVerificationPending() {
    return this.newsletterVerificationPending;
  }

  public getCropperSettings() {
    return null;
  }

  public getHomePath() {
    return this.homePath;
  }

  public getLoading() {
    return this.loading;
  }


  public isDirty() {
    return this.updateMyProfileForm.dirty || this.filesModified;
  }

  @HostListener('window:beforeunload')
  canUnloadWindow() {
    return (!this.updateMyProfileForm.dirty && !this.filesModified) || !this.isNotDeleteNavigation;
  }

  onSelected(option) {
    this.selectWithInitialValue = true;
  }


  onDeselected(option) {
    this.selectWithInitialValue = false;
  }


  public updateProfile(formValues: any) {
    this.loading = true;
    let newProfileData: Profile = new Profile();
    if (!this.readonly) {
      newProfileData = this.getUserProfileModel(formValues, this.loggedInUserProfile.ldapUser);
    } else {
      newProfileData.firstName = this.loggedInUserProfile.firstName;
      newProfileData.lastName = this.loggedInUserProfile.lastName;
      newProfileData.email = this.loggedInUserProfile.email;
      newProfileData.language = this.updateMyProfileForm.controls['language'].value;
      newProfileData.countryCode = this.updateMyProfileForm.controls['countryCode'].value;
      newProfileData.postalCode = this.updateMyProfileForm.controls['postalCode'].value;
      // newProfileData.receiveNewsletter = this.updateMyProfileForm.controls['wantsNewsletter'].value;
    }
    newProfileData.id = this.loggedInUserProfile.id;

    let sidebarUserProfileImage = $('#user-profile-image');
    if (this.removeAvatar && (!this.loggedInUserProfile.avatar && !this.updateMyProfileForm.controls.avatar.value)) {
      this._updateProfileService.updateUserProfile(newProfileData, null, this.removeAvatar).subscribe((res) => {
        this.loading = false;
        this._actionAfterUpdateProfile(res);
        if (sidebarUserProfileImage) {
          sidebarUserProfileImage.attr('src', '');
          sidebarUserProfileImage.parent().addClass('d-none');
          $('.user-icon-wrap').parent().removeClass('d-none');
        }
      }, (error) => {
        this.loading = false;
      });
    } else if (this.loggedInUserProfile.avatar == this.imageFromDB) {
      this._updateProfileService.updateUserProfile(newProfileData).subscribe((res) => {
        this.loading = false;
        this._actionAfterUpdateProfile(res);
      }, (error) => {
        this.loading = false;
      });
    } else {
      let imageUploadData: ImageModel = new ImageModel();

      if (this.base64Image !== null && this.base64Image.length !== 0) {
        imageUploadData.content = this.base64Image;
        imageUploadData.contentType = this.loggedInUserProfile.avatarContentType;
        imageUploadData.name = this.loggedInUserProfile.avatarName;
        imageUploadData.sizeInBytes = this.getSizeOfImageFromBase64(this.base64Image);
      } else {
        imageUploadData = null;
      }

      this._updateProfileService.updateUserProfile(newProfileData, imageUploadData).subscribe((res) => {
        this.loading = false;
        this._actionAfterUpdateProfile(res);
        if (sidebarUserProfileImage) {
          sidebarUserProfileImage.attr('src', this.loggedInUserProfile.avatar);
          sidebarUserProfileImage.parent().removeClass('d-none');
          $('.user-icon-wrap').parent().addClass('d-none');
        }
      }, (error) => {
        this.loading = false;
      });
    }
  }

  private getSizeOfImageFromBase64(baseString) {
    // Split the base64 string in data and contentType
    const block = baseString.split(';');
    // get the real base64 content of the file (ex.: iVBORw0KGg....)
    const realData = block[1].split(',')[1];
    return atob(realData).length;
  }

  public resendNewsletterSubscriptionLink() {
    this._updateProfileService.resendNewsletterSubscriptionLink().subscribe();
  }


  public deleteMyProfile(confirmation) {
    if (confirmation) {
      this._updateProfileService.deleteProfile().toPromise()
        .then(res => {
          this.isNotDeleteNavigation = false;
          this._utilsService.logout();
        })
        .catch(err => console.log(err));
    } else {
      return
    }
  }


  public navigateToChangePassword() {
    this._router.navigateByUrl(AppConstants.myProfilechangePassword)
  }

  public deleteAvatar() {
    this.filesModified = true;
    if (this.loggedInUserProfile.avatar) {
      this.removeAvatar = true;
      this.loggedInUserProfile.avatar = null;
    }
    this.updateMyProfileForm.controls.avatar.reset();
    this.loggedInUserProfile.avatar = null;
  }

  private _initFormWithExistingUserData() {
    if (this.loggedInUserProfile.salutation && !this.titles.includes(this.loggedInUserProfile.salutation)) {
      this.titles.push(this.loggedInUserProfile.salutation);
      this.listOfTitles = this._utilsService.initializeListOfSalutations(this.titles);
    }
    this.updateMyProfileForm.controls.title.setValue(this.loggedInUserProfile.salutation);
    this.updateMyProfileForm.controls.firstName.setValue(this.loggedInUserProfile.firstName);
    this.updateMyProfileForm.controls.lastName.setValue(this.loggedInUserProfile.lastName);
    this.updateMyProfileForm.controls.email.setValue(this.loggedInUserProfile.email);
    if (this.loggedInUserProfile.birthDate != null) {
      this.updateMyProfileForm.controls.birthDate.setValue(this._utilsService.convertIsoDateStringToNgbDate(this.loggedInUserProfile.birthDate));
    }
    this.updateMyProfileForm.controls.phone.setValue(this.loggedInUserProfile.phone);
    this.updateMyProfileForm.controls.language.setValue(this.loggedInUserProfile.language);
    this.updateMyProfileForm.controls.countryCode.setValue(this.loggedInUserProfile.countryCode);
    this.updateMyProfileForm.controls.postalCode.setValue(this.loggedInUserProfile.postalCode);
    this.setupPostalCodeValidators(this.loggedInUserProfile.targetGroup === AppConstants.architectPlanner);
    this.updateMyProfileForm.controls.roles.setValue(this.loggedInUserProfile.roles);
    this.updateMyProfileForm.controls.roles.disable({onlySelf: true});
    this.updateMyProfileForm.controls.avatar.setValue(this.loggedInUserProfile.avatar);
    // this.updateMyProfileForm.controls.wantsNewsletter.setValue(this.loggedInUserProfile.receiveNewsletter);
  }


  private _initAndDisableCompanyFields() {
    this.updateMyProfileForm.controls.customerNr.setValue(this.loggedInUserProfile.customerNumber);
    this.updateMyProfileForm.controls.customerNr.disable({onlySelf: true});
    this.updateMyProfileForm.controls.company.setValue(this.loggedInUserProfile.customerName);
    this.updateMyProfileForm.controls.company.disable({onlySelf: true});
    this.updateMyProfileForm.controls.companyLogo.setValue(this.loggedInUserProfile.companyLogo);
    this.updateMyProfileForm.controls.companyLogo.disable({onlySelf: true});
    if (this.loggedInUserProfile.contactPerson != null) {
      this.updateMyProfileForm.controls.contactPerson.setValue(
        this._utilsService.concatFirstNameWithLastName(
          this.loggedInUserProfile.contactPerson.firstName,
          this.loggedInUserProfile.contactPerson.lastName)
      );
    }
    this.updateMyProfileForm.controls.contactPerson.disable({onlySelf: true});
  }

  private setupPostalCodeValidators(required) {
    this.updateMyProfileForm.controls.postalCode.setValidators([]);
    if (required) {
      this.postalCodeRequired = true;
      this.updateMyProfileForm.controls.postalCode.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this._utilsService.getPostalCodeRegex())
      ]);
    } else {
      this.postalCodeRequired = false;
      this.updateMyProfileForm.controls.postalCode.setValidators([
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this._utilsService.getPostalCodeRegex())
      ]);
    }
    this.updateMyProfileForm.controls.postalCode.markAsUntouched();
    this.updateMyProfileForm.controls.postalCode.updateValueAndValidity();
  }

  private _disableForbiddenLdapUserFields() {
    this.updateMyProfileForm.controls.title.disable({onlySelf: true});
    this.updateMyProfileForm.controls.firstName.disable({onlySelf: true});
    this.updateMyProfileForm.controls.lastName.disable({onlySelf: true});
    this.updateMyProfileForm.controls.email.disable({onlySelf: true});
    this.updateMyProfileForm.controls.birthDate.disable({onlySelf: true});
    this.updateMyProfileForm.controls.phone.disable({onlySelf: true});
  }

  private _actionAfterUpdateProfile(updatedProfile: Profile) {
    const incomingLang = updatedProfile.language.split('.')[1];
    if (incomingLang) {
      this._translateService.use(incomingLang);
      translateApp(incomingLang);
    }

    this.updateMyProfileForm.markAsPristine();
    this.filesModified = false;

    this.confirmationModalRef = this._modalService.show(ConfirmationModal);
    this._translateService.get('Profile was updated successfully').subscribe((res: string) => {
      this.confirmationModalRef.content.confirmationMessage = res;
    });

    // if (updatedProfile.receiveNewsletter) {
    //   this.newsletterVerificationPending = updatedProfile.receiveNewsletter && updatedProfile.agreedToReceiveNewsletter == null;
    // } else {
    //   this.newsletterVerificationPending = false;
    // }

  }

  fileChangeEvent(event: any): void {
    this.imageChangedEvent = event;

    this.filesModified = true;
    if (event.target.files && event.target.files[0]) {
      this.loggedInUserProfile.avatarName = event.target.files[0].name;
      this.loggedInUserProfile.avatarContentType = event.target.files[0].type;
    }

  }

  imageCropped(event: ImageCroppedEvent) {
    this.loggedInUserProfile.avatar = event.objectUrl;
    const reader = new FileReader();
    reader.readAsDataURL(event.blob);
    reader.onload = _event => {
      this.base64Image = reader.result.toString();
    };

  }

  imageLoaded(image: LoadedImage) {
    // show cropper
  }

  cropperReady() {
    // cropper ready
  }

  loadImageFailed() {
    // show message
  }

  async getBase64ImageFromUrl(imageUrl) {
    var res = await fetch(imageUrl);
    var blob = await res.blob();

    return new Promise((resolve, reject) => {
      var reader = new FileReader();
      reader.addEventListener("load", function () {
        resolve(reader.result);
      }, false);

      reader.onerror = () => {
        return reject(this);
      };
      reader.readAsDataURL(blob);
    })
  }

  private getUserProfileModel(formValues, isLdapUser): Profile {
    const newProfileData: Profile = new Profile();
    newProfileData.salutation = formValues.title;
    newProfileData.firstName = formValues.firstName;
    newProfileData.lastName = formValues.lastName;
    newProfileData.email = formValues.email;
    newProfileData.phone = formValues.phone;
    newProfileData.birthDate = this._utilsService.convertNgbDateToIsoDateString(formValues.birthDate);
    newProfileData.language = formValues.language;
    newProfileData.countryCode = formValues.countryCode;
    newProfileData.ldapUser = isLdapUser;
    // newProfileData.receiveNewsletter = formValues.wantsNewsletter;
    newProfileData.postalCode = formValues.postalCode;
    return newProfileData;
  }

  private getDateFromInput(dateRepresentation: any) {
    if (dateRepresentation != null) {
      return new Date(dateRepresentation.year, dateRepresentation.month - 1, dateRepresentation.day)
    }
  }

}
